// src/utils/notificationUtils.js

/**
 * Utility functions for managing push notifications,
 * specifically designed to work with iOS home screen web app (PWA)
 * Now with WebSocket fallback for real-time notifications
 */

import webSocketClient from '../WebSocketClient';
import { apiFetch, getWebSocketUrl, apiNotificationFetch } from './apiUtils';

const PUBLIC_VAPID_KEY = 'BPOX2Png4uZ2_tDeXUeIsb86QQvIi6gxY_OwEpeMp71kAnKgKTDgtRZfR_XKf_3kUPvxS5_nbme96svbXbT7HAI';

/**
 * Detect iOS device
 * @returns {boolean} True if current device is running iOS
 */
export const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/**
 * Check if the browser supports push notifications
 * @returns {boolean} True if the browser supports push notifications
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if notifications are permitted, denied, or prompt is needed
 * @returns {Promise<string>} "granted", "denied", or "default" (need to ask)
 */
export const getNotificationPermissionStatus = async () => {
  // First check if the browser supports Notification API
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  // Return current permission state
  return Notification.permission;
};

/**
 * Request permission for notifications
 * @returns {Promise<boolean>} True if permission was granted
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }
  
  // If permission is already granted, return true
  if (Notification.permission === 'granted') {
    return true;
  }
  
  // If permission is already denied, return false
  if (Notification.permission === 'denied') {
    console.log('Notification permission has been denied');
    return false;
  }
  
  // Request permission
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get the service worker registration
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const getServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers are not supported');
    return null;
  }
  
  try {
    return await navigator.serviceWorker.ready;
  } catch (error) {
    console.error('Error getting service worker registration:', error);
    return null;
  }
};

/**
 * Convert a base64 string to an Uint8Array
 * Critical for proper VAPID key format on all browsers including Safari
 * @param {string} base64String 
 * @returns {Uint8Array}
 */
const urlBase64ToUint8Array = (base64String) => {
  // Make sure we have a string
  if (typeof base64String !== 'string') {
    console.error('VAPID key must be a string, got:', typeof base64String);
    throw new Error('Invalid VAPID key format');
  }
  
  console.log('⚠️ VAPID key being processed:', base64String.substring(0, 10) + '...');
  
  // Add padding if needed
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  try {
    // Convert base64 to binary
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    // Verify the output array
    if (outputArray.length < 8) {
      console.error('⚠️ Generated applicationServerKey is too short:', outputArray.length);
    } else {
      console.log('✅ Successfully generated applicationServerKey with length:', outputArray.length);
    }
    
    return outputArray;
  } catch (error) {
    console.error('❌ Error converting VAPID key:', error);
    throw new Error('Failed to process VAPID key: ' + error.message);
  }
};

/**
 * Subscribe to push notifications using the standard Web Push API
 * This is a simplified implementation that works across browsers including iOS Safari
 * @returns {Promise<PushSubscription|null>} The push subscription or null if not supported/failed
 */
export const subscribeToPushNotifications = async () => {
  // First make sure the browser supports push notifications
  if (!isPushSupported()) {
    console.log('Push notifications are not supported in this browser');
    return null;
  }
  
  // PWA check for iOS - Web Push only works when running as a PWA from the home screen
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator.standalone === true);
                
  // Log device information (useful for debugging)
  console.log('📱 Device Info:', {
    isIOS: isIOS(),
    isPWA: isPWA,
    userAgent: navigator.userAgent.substring(0, 50) + '...',
    standalone: window.navigator.standalone
  });
  
  // Warn if this is iOS but not running as a PWA
  if (isIOS() && !isPWA) {
    console.warn('⚠️ This is an iOS device but not running as a PWA from the home screen');
    console.warn('⚠️ Web Push notifications require the app to be added to the home screen');
    // Continue anyway in case it works, but log the warning
  }
  
  // Request notification permission - required for all browsers
  const permissionGranted = await requestNotificationPermission();
  if (!permissionGranted) {
    console.log('Cannot subscribe without notification permission');
    return null;
  }
  
  // Get service worker registration
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    console.log('No service worker registration found');
    return null;
  }
  
  // Prepare application server key - this is CRITICAL for iOS Safari
  console.log('📱 Preparing application server key from VAPID key:', PUBLIC_VAPID_KEY.substring(0, 10) + '...');
  
  // Validate the VAPID key format
  if (!PUBLIC_VAPID_KEY || PUBLIC_VAPID_KEY.length < 20) {
    console.error('❌ Invalid VAPID public key:', PUBLIC_VAPID_KEY);
    throw new Error('Invalid VAPID public key format');
  }
  
  // Convert the key using our enhanced function
  const applicationServerKey = urlBase64ToUint8Array(PUBLIC_VAPID_KEY);
  
  // Verify we have a proper Uint8Array for the key
  if (!(applicationServerKey instanceof Uint8Array) || applicationServerKey.length < 8) {
    console.error('❌ Invalid applicationServerKey format or length:', 
                 applicationServerKey instanceof Uint8Array ? 'Uint8Array' : typeof applicationServerKey,
                 applicationServerKey.length);
    throw new Error('Invalid applicationServerKey generated');
  }
  
  console.log('✅ Successfully prepared applicationServerKey with length:', applicationServerKey.length);
  
  let subscription;
  // Standard attempt loop for all browsers
  try {
    // Check for and unsubscribe from any existing subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('Unsubscribing from existing push subscription to get a fresh one');
      await existingSubscription.unsubscribe();
    }
    
    // Create a new subscription using standard W3C Push API
    // This works the same on all browsers including Safari on iOS 16.4+
    console.log('Creating push subscription...');
    
    // CRITICAL: Use await and handle promise rejection properly
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // Required for all browsers
      applicationServerKey: applicationServerKey // VAPID public key - must be Uint8Array
    });
    
    // Log success
    console.log('✅ Successfully created push subscription');
    console.log('Subscription endpoint:', subscription.endpoint ? subscription.endpoint.substring(0, 50) + '...' : 'none');
    
    // CRITICAL: Comprehensive key validation and logging
    console.log('CRITICAL DEBUGGING - KEY EXTRACTION PROCESS:');
    
    // Method 1: Using getKey method directly (if available)
    let p256dhKey, authKey;
    let keysExtracted = false;
    
    try {
      if (subscription.getKey) {
        console.log('Attempting to extract keys using getKey() method...');
        const p256dhBuffer = subscription.getKey('p256dh');
        const authBuffer = subscription.getKey('auth');
        
        console.log('Key buffers:', {
          p256dhExists: Boolean(p256dhBuffer),
          authExists: Boolean(authBuffer),
          p256dhType: p256dhBuffer ? (p256dhBuffer instanceof ArrayBuffer ? 'ArrayBuffer' : typeof p256dhBuffer) : 'null',
          authType: authBuffer ? (authBuffer instanceof ArrayBuffer ? 'ArrayBuffer' : typeof authBuffer) : 'null',
          p256dhLength: p256dhBuffer instanceof ArrayBuffer ? p256dhBuffer.byteLength : 0,
          authLength: authBuffer instanceof ArrayBuffer ? authBuffer.byteLength : 0
        });
        
        // Convert ArrayBuffer to Base64 string if needed
        if (p256dhBuffer && authBuffer) {
          if (p256dhBuffer instanceof ArrayBuffer && authBuffer instanceof ArrayBuffer) {
            const p256dhArray = new Uint8Array(p256dhBuffer);
            const authArray = new Uint8Array(authBuffer);
            
            // Create base64 strings from buffers
            p256dhKey = btoa(String.fromCharCode.apply(null, p256dhArray));
            authKey = btoa(String.fromCharCode.apply(null, authArray));
            
            console.log('Successfully converted key buffers to Base64:', {
              p256dhLength: p256dhKey.length,
              authLength: authKey.length,
              p256dhPreview: p256dhKey.substring(0, 10) + '...',
              authPreview: authKey.substring(0, 10) + '...'
            });
            
            keysExtracted = true;
          } else {
            console.log('Keys are not ArrayBuffers, checking their format...');
            // If they're already strings, use them directly
            if (typeof p256dhBuffer === 'string' && typeof authBuffer === 'string') {
              p256dhKey = p256dhBuffer;
              authKey = authBuffer;
              console.log('Keys appear to be string format already');
              keysExtracted = true;
            }
          }
        }
      } else {
        console.log('⚠️ subscription.getKey method is not available');
      }
    } catch (keyError) {
      console.error('Error during key extraction via getKey method:', keyError);
    }
    
    // Method 2: Using toJSON method
    if (!keysExtracted) {
      try {
        console.log('Attempting to extract keys using toJSON() method...');
        if (subscription.toJSON) {
          const json = subscription.toJSON();
          
          // Log what keys are available in the JSON
          const keysAvailable = json.keys ? Object.keys(json.keys) : [];
          console.log('Keys available in JSON:', keysAvailable);
          
          console.log('JSON structure check:', {
            hasEndpoint: Boolean(json.endpoint),
            hasKeys: Boolean(json.keys),
            hasP256dh: Boolean(json.keys && json.keys.p256dh),
            hasAuth: Boolean(json.keys && json.keys.auth)
          });
          
          if (json.keys && json.keys.p256dh && json.keys.auth) {
            p256dhKey = json.keys.p256dh;
            authKey = json.keys.auth;
            console.log('Successfully extracted keys from JSON:', {
              p256dhLength: p256dhKey.length,
              authLength: authKey.length,
              p256dhPreview: p256dhKey.substring(0, 10) + '...',
              authPreview: authKey.substring(0, 10) + '...'
            });
            keysExtracted = true;
          } else {
            console.log('⚠️ Keys missing from JSON structure');
            // Try stringifying the full subscription to see what's there
            const fullJson = JSON.stringify(subscription);
            console.log('Full subscription stringify preview:', fullJson.substring(0, 100) + '...');
            console.log('Full subscription stringify length:', fullJson.length);
          }
        } else {
          console.log('⚠️ subscription.toJSON method is not available');
        }
      } catch (jsonError) {
        console.error('Error during key extraction via toJSON method:', jsonError);
      }
    }
    
    // Method 3: Direct property access as last resort
    if (!keysExtracted) {
      try {
        console.log('Attempting direct property access as last resort...');
        // @ts-ignore - Accessing properties that might not be standard
        if (subscription.keys) {
          // @ts-ignore
          const directKeys = subscription.keys;
          console.log('Direct keys property check:', {
            exists: Boolean(directKeys),
            type: typeof directKeys,
            isObject: directKeys instanceof Object,
            properties: directKeys ? Object.keys(directKeys) : []
          });
          
          // @ts-ignore
          if (directKeys && directKeys.p256dh && directKeys.auth) {
            // @ts-ignore
            p256dhKey = directKeys.p256dh;
            // @ts-ignore
            authKey = directKeys.auth;
            console.log('Successfully extracted keys via direct property access');
            keysExtracted = true;
          }
        }
      } catch (directError) {
        console.error('Error during direct property access:', directError);
      }
    }
    
    // Final validation of keys
    if (keysExtracted && p256dhKey && authKey) {
      console.log('✅ KEYS SUCCESSFULLY EXTRACTED using method', 
                 keysExtracted ? 'standard' : 'fallback');
      
      // Store successful extraction for debugging
      localStorage.setItem('push_keys_extracted', 'true');
      localStorage.setItem('push_keys_extracted_time', new Date().toISOString());
      
      // Create an enhanced subscription object that guarantees keys will be included
      // This ensures the server always receives the keys in the format it expects
      const enhancedSubscription = {
        endpoint: subscription.endpoint,
        expirationTime: subscription.expirationTime,
        keys: {
          p256dh: p256dhKey,
          auth: authKey
        },
        // Maintain the original methods but ensure keys are included in toJSON
        getKey: subscription.getKey?.bind(subscription),
        toJSON: () => ({
          endpoint: subscription.endpoint,
          expirationTime: subscription.expirationTime,
          keys: {
            p256dh: p256dhKey,
            auth: authKey
          }
        }),
        unsubscribe: subscription.unsubscribe?.bind(subscription)
      };
      
      console.log('Created enhanced subscription object with guaranteed keys');
      
      // Store basic info for session management
      localStorage.setItem('push_subscription_active', 'true');
      localStorage.setItem('push_subscription_created', Date.now().toString());
      localStorage.setItem('push_subscription_has_keys', 'true');
      
      return enhancedSubscription;
    } else {
      console.error('❌ CRITICAL ERROR: Failed to extract keys through any method');
      localStorage.setItem('push_keys_extraction_failed', 'true');
      localStorage.setItem('push_keys_extraction_failed_time', new Date().toISOString());
      
      // Log the raw subscription for debugging
      console.log('Raw subscription type:', typeof subscription);
      console.log('Raw subscription prototype:', 
                  subscription ? Object.getPrototypeOf(subscription)?.constructor?.name : 'null');
      console.log('Raw subscription properties:', 
                  subscription ? Object.getOwnPropertyNames(subscription) : []);
      
      // Since we couldn't extract keys, the subscription won't work properly
      // Return the original subscription for now, but it likely won't work
      return subscription;
    }
  } catch (error) {
    console.error('Error creating push subscription:', error);
    
    // Specific error handling for common issues
    if (error.name === 'NotAllowedError') {
      console.log('User denied permission for push notifications');
    } else if (error.name === 'InvalidStateError') {
      console.log('The service worker is not activated yet');
    }
    
    // Clean up if subscription failed
    try {
      const failedSub = await registration.pushManager.getSubscription();
      if (failedSub) {
        await failedSub.unsubscribe();
      }
    } catch (cleanupError) {
      // Ignore cleanup errors
    }
    
    return null;
  }
};

/**
 * Get comprehensive device information to identify each subscription
 * With enhanced iOS detection and debugging details
 * @param {boolean} forceIOS - Override detection and force identify as iOS (for testing)
 * @returns {Object} Detailed device information
 */
export const getDeviceInfo = (forceIOS = false) => {
  // Detect iOS using our enhanced detection function
  const detectedIOS = isIOS();
  
  // Special check for misidentified devices in user agent
  const userAgent = navigator.userAgent.toLowerCase();
  const hasiOSKeywords = userAgent.includes('iphone') || 
                         userAgent.includes('ipad') || 
                         userAgent.includes('ipod') ||
                         navigator.platform === 'iPhone' ||
                         navigator.platform === 'iPad';
  
  // Check if this could be an iPhone misidentified as Win32
  const isPotentiallyMisidentifiediOS = hasiOSKeywords && 
                                       (userAgent.includes('win32') || 
                                        navigator.platform === 'Win32');
                                        
  // Check notification permission - critical for Web Push API support
  const notificationPermission = 'Notification' in window ? Notification.permission : 'unsupported';
  const hasNotificationPermission = notificationPermission === 'granted';
  
  // Get iOS version info if detected
  const iosVersionInfo = detectedIOS ? getIOSVersion() : null;
  
  // Determine if this is a PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
               (window.navigator.standalone === true);
  
  // Final iOS flag, can be forced for testing
  // Also consider potential misidentified devices
  const finalIsIOS = forceIOS || detectedIOS || isPotentiallyMisidentifiediOS;
  
  // Get device name from local storage or generate one
  let deviceName = localStorage.getItem('deviceName');
  
  // CRITICAL: If device name is Win32 Browser but forceIOS is true, we need to regenerate
  if (deviceName && deviceName.includes('Win32 Browser') && forceIOS) {
    // Clear it to force regeneration with iPhone name
    localStorage.removeItem('deviceName');
    deviceName = null;
    console.log('⚠️ Clearing Win32 Browser device name due to force iOS flag');
  }
  
  if (!deviceName) {
    // For iOS devices or forced iOS, create a more specific name
    if (finalIsIOS) {
      // Try to find a model identifier - fallback to generic
      const model = /iPhone|iPad|iPod/.exec(navigator.userAgent)?.[0] || 'iOS Device';
      const randomId = Math.floor(Math.random() * 10000);
      deviceName = `${model} ${randomId}`;
    } else {
      deviceName = `Browser on ${navigator.platform}`;
    }
    localStorage.setItem('deviceName', deviceName);
    console.log('Generated new device name:', deviceName);
  }
  
  // Log information for debugging
  if (forceIOS && !detectedIOS) {
    console.log('⚠️ iOS detection was manually forced for this device');
  }
  
  if (isPotentiallyMisidentifiediOS) {
    console.log('⚠️ Potential iPhone misidentified as Win32 detected in User Agent');
  }
  
  return {
    // Basic device info
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio || 1,
    
    // iOS specific flags
    isIOS: finalIsIOS,
    detectedAsIOS: detectedIOS, // For debugging
    forcedIOS: forceIOS, // For debugging
    potentiallyMisidentifiediOS: isPotentiallyMisidentifiediOS, // For debugging
    iosVersion: iosVersionInfo?.version,
    // Critical fix: Only claim Web Push API support if permission is granted
    usingWebPushAPI: finalIsIOS && 
                     (iosVersionInfo?.supportsWebPush || forceIOS) && 
                     hasNotificationPermission,
    
    // Environment info
    isPWA,
    standalone: window.navigator.standalone === true,
    language: navigator.language,
    timestamp: new Date().toISOString(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // Identification
    deviceName,
    
    // Browser capabilities
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPushManager: 'PushManager' in window,
    hasNotification: 'Notification' in window,
    notificationPermission: 'Notification' in window ? Notification.permission : 'unsupported'
  };
};

/**
 * Save subscription to the server
 * @param {PushSubscription} subscription 
 * @param {string} userId 
 * @returns {Promise<boolean>} True if saved successfully
 */
export const saveSubscription = async (subscription, userId) => {
  if (!subscription) {
    console.error('No subscription to save');
    return false;
  }
  
  try {
    // Get device information to help identify this subscription
    const deviceInfo = getDeviceInfo();
    
    // IMPORTANT: Extract keys from subscription object to ensure they're properly included
    // This is critical for iOS Safari which might handle subscription objects differently
    let p256dhKey = null;
    let authKey = null;
    let endpoint = subscription.endpoint;
    let expirationTime = subscription.expirationTime;
    
    // Extract keys using multiple methods to ensure we get them
    console.log('Extracting keys for server submission...');
    
    // Method 1: Extract via toJSON if available
    try {
      if (subscription.toJSON) {
        const json = subscription.toJSON();
        console.log('Keys in JSON:', json.keys ? Object.keys(json.keys) : 'none');
        
        if (json.keys && json.keys.p256dh && json.keys.auth) {
          p256dhKey = json.keys.p256dh;
          authKey = json.keys.auth;
          console.log('Extracted keys from JSON method');
        }
      }
    } catch (jsonError) {
      console.error('Error extracting keys from JSON:', jsonError);
    }
    
    // Method 2: Extract via getKey if available and needed
    if (!p256dhKey || !authKey) {
      try {
        if (subscription.getKey) {
          const p256dhBuffer = subscription.getKey('p256dh');
          const authBuffer = subscription.getKey('auth');
          
          if (p256dhBuffer && authBuffer) {
            // Convert ArrayBuffer to Base64 string
            const p256dhArray = new Uint8Array(p256dhBuffer);
            const authArray = new Uint8Array(authBuffer);
            
            p256dhKey = btoa(String.fromCharCode.apply(null, p256dhArray));
            authKey = btoa(String.fromCharCode.apply(null, authArray));
            console.log('Extracted keys from getKey method');
          }
        }
      } catch (keyError) {
        console.error('Error extracting keys from getKey:', keyError);
      }
    }
    
    // Check if we have the keys
    if (p256dhKey && authKey) {
      console.log('✅ Successfully extracted keys for server submission');
      
      // Log key details for debugging
      console.log('Key details:', {
        p256dhLength: p256dhKey.length,
        authLength: authKey.length,
        p256dhPreview: p256dhKey.substring(0, 10) + '...',
        authPreview: authKey.substring(0, 10) + '...',
        endpoint: endpoint.substring(0, 30) + '...'
      });
    } else {
      console.error('❌ Failed to extract keys from subscription object');
    }
    
    // Create a subscription object with explicitly included keys for server
    const subscriptionForServer = {
      endpoint,
      expirationTime,
      keys: {
        p256dh: p256dhKey,
        auth: authKey
      }
    };
    
    console.log('💾 Saving subscription with explicit keys using direct notification API call');
    const response = await apiNotificationFetch('/api/notifications/subscription', {
      method: 'POST',
      body: JSON.stringify({
        subscription: subscriptionForServer, // Use our processed version with explicit keys
        userId,
        deviceInfo,
        // Add debug info for server-side logging
        debug: {
          hasKeys: Boolean(p256dhKey && authKey),
          keyMethod: p256dhKey && authKey ? 'explicit' : 'original',
          browser: navigator.userAgent.substring(0, 50)
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to save subscription: ${response.status} ${response.statusText}`);
    }
    
    console.log('Subscription saved successfully for device', deviceInfo.deviceName);
    return true;
  } catch (error) {
    console.error('Error saving subscription:', error);
    return false;
  }
};

/**
 * Update subscription for a specific user
 * @param {string} userId 
 * @param {boolean} forceIOS - Force iOS mode for this subscription (overrides detection)
 * @returns {Promise<boolean>} True if updated successfully
 */
export const updateUserSubscription = async (userId, forceIOS = false) => {
  // Ensure userId is defined
  if (!userId) {
    console.error('Cannot update subscription: No user ID provided');
    return false;
  }
  
  // Normalize userId to ensure consistency with server lookups
  // The server's getUserSubscriptions function maps usernames to first names
  const normalizedUserId = userId.includes(' ') ? userId.split(' ')[0] : userId;
  if (normalizedUserId !== userId) {
    console.log(`🔄 Normalizing user ID from "${userId}" to "${normalizedUserId}" for subscription`);
    userId = normalizedUserId;
  }
  
  console.log(`Updating subscription for ${userId}${forceIOS ? ' with FORCED iOS mode' : ''}`);
  
  // Check if subscription needs refreshing based on age
  const now = Date.now();
  const lastUpdate = parseInt(localStorage.getItem('push_subscription_last_update') || '0');
  const subscriptionTTL = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
  
  // Force refresh if subscription is older than TTL
  const needsRefresh = (now - lastUpdate) > subscriptionTTL;
  
  // Skip only if we've already subscribed in this session AND not forcing iOS AND no refresh needed
  if (localStorage.getItem('push_subscription_active') === 'true' && 
      localStorage.getItem('push_subscription_user') === userId &&
      !forceIOS && !needsRefresh) {
    console.log('Already subscribed for this user and subscription is fresh');
    return true;
  }
  
  // Get device info, with potential force flag
  const deviceInfo = getDeviceInfo(forceIOS);
  
  // Log detailed device info for debugging
  console.log('📱 DETAILED DEVICE INFO:', {
    isIOS: deviceInfo.isIOS,
    forcedIOS: deviceInfo.forcedIOS,
    potentiallyMisidentified: deviceInfo.potentiallyMisidentifiediOS,
    usingWebPushAPI: deviceInfo.usingWebPushAPI,
    deviceName: deviceInfo.deviceName,
    platform: deviceInfo.platform,
    userAgent: deviceInfo.userAgent.slice(0, 50) + '...' // Truncate for readability
  });
  
  // Special handling for devices that were misidentified or forced
  const useWebPushAPI = forceIOS || (deviceInfo.isIOS && deviceInfo.usingWebPushAPI);
  
  if (useWebPushAPI) {
    console.log('📱 Using Web Push API for this device (iOS 16.4+ or forced iOS mode)');
    
    // Try to use the standard Web Push API flow
    try {
      // Add enhanced details
      const enhancedDeviceInfo = {
        ...deviceInfo,
        isIOS: true, // Force flag to true for server side
        iOSVersion: deviceInfo.iosVersion || 'unknown',
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        usingWebPushAPI: true
      };
      
      console.log('📱 Attempting Web Push subscription...');
      const subscription = await subscribeToPushNotifications();
      
      if (subscription) {
        console.log('✅ Successfully created Web Push subscription');
        
        // Save the subscription with our enhanced device info
        const saved = await saveSubscription(subscription, userId);
        if (saved) {
          console.log('✅ Successfully saved Web Push subscription');
          localStorage.setItem('ios_push_registration', 'true');
          localStorage.setItem('ios_using_web_push', 'true');
          localStorage.setItem('ios_push_last_update', new Date().toISOString());
          
          // For debugging - track if this was a forced override
          if (forceIOS) {
            localStorage.setItem('subscription_force_overridden', 'true');
            localStorage.setItem('subscription_force_timestamp', new Date().toISOString());
          }
          
          return true;
        }
      } else {
        console.log('❌ Failed to create Web Push subscription');
      }
    } catch (error) {
      console.error('❌ Error using Web Push API:', error);
      // Fall through to WebSocket fallback if Web Push fails
    }
  }
  
  // For devices that don't support Web Push API (older iOS or when forced mode fails)
  if (deviceInfo.isIOS || forceIOS) {
    console.log('📱 Using WebSocket fallback for device');
    
    try {
      // Get a fresh copy of device info with force flag
      const deviceInfo = getDeviceInfo(forceIOS);
      
      // Add more device-specific details to help identify the device
      const enhancedDeviceInfo = {
        ...deviceInfo,
        isIOS: true, // Force this flag to true for server-side
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        devicePixelRatio: window.devicePixelRatio || 1,
        orientation: window.screen.orientation ? window.screen.orientation.type : 'unknown',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        forcedRegistration: forceIOS
      };
      
      // Fall back to WebSocket-based notifications since Web Push isn't available or failed
      console.log('📱 Using WebSocket fallback for notifications');
      return await registerWebSocketFallback(userId, enhancedDeviceInfo);
      
    } catch (error) {
      console.error('❌ Fatal error registering device:', error);
      return false;
    }
  }


/**
 * Register device for WebSocket fallback notifications (for older iOS versions)
 * This function is used instead of APNs for iOS devices that don't support Web Push API
 * @param {string} userId - User ID
 * @param {Object} deviceInfo - Device information
 * @returns {Promise<boolean>} Whether registration was successful
 */
async function registerWebSocketFallback(userId, deviceInfo) {
  console.log('📱 Registering device for WebSocket fallback notifications');
  
  let attempts = 0;
  const maxAttempts = 3;
  
  while (attempts < maxAttempts) {
    attempts++;
    try {
      console.log(`📱 WebSocket fallback registration attempt ${attempts}/${maxAttempts}`);
      
      const response = await apiNotificationFetch('/api/notifications/subscription', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          deviceInfo,
          forceCreate: attempts > 1 // Try forcing new record on retry attempts
        })
      });
      
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`Failed to register for WebSocket notifications: ${response.status} - ${responseText}`);
      }
      
      console.log('✅ Device registered for WebSocket notifications');
      
      localStorage.setItem('websocket_registration', 'true');
      localStorage.setItem('websocket_registration_timestamp', new Date().toISOString());
      
      return true;
    } catch (error) {
      console.error(`📱 WebSocket fallback registration attempt ${attempts} error:`, error);
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  console.error('❌ All WebSocket fallback registration attempts failed');
  return false;
}
  
  // For non-iOS devices, use the standard approach
  try {
    const subscription = await subscribeToPushNotifications();
    if (!subscription) {
      return false;
    }
    
    // Get device information to help identify this subscription
    const deviceInfo = getDeviceInfo();
    
    // Pass both subscription and device info to saveSubscription
    return await saveSubscription(subscription, userId);
  } catch (error) {
    console.error('Error updating user subscription:', error);
    return false;
  }
};

/**
 * Send a test notification via the service worker
 * @returns {Promise<boolean>} True if notification was sent
 */
export const sendTestNotification = async () => {
  // Check if this is iOS 16.4+ which supports Web Push API
  const iOS164OrAbove = isIOS() && parseInt(
    (navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/) || [])[1]
  ) >= 16 && parseInt(
    (navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/) || [])[2] || 0
  ) >= 4;
  
  // Skip for older iOS devices (below 16.4)
  if (isIOS() && !iOS164OrAbove) {
    console.log('Test notification skipped on older iOS device (below 16.4)');
    return false;
  }
  
  // Special log for iOS 16.4+ devices
  if (isIOS() && iOS164OrAbove) {
    console.log('📱 iOS 16.4+ detected - proceeding with test notification via Web Push API');
  }
  
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    console.log('No service worker registration found');
    return false;
  }
  
  try {
    await registration.showNotification('Test Notification', {
      body: 'This is a test notification',
      icon: '/zoomingroomin_logo-paw.png',
      badge: '/logo192.png',
      data: {
        dateOfArrival: Date.now(),
        primaryKey: 1
      },
      actions: [
        { action: 'explore', title: 'Go to App' }
      ]
    });
    return true;
  } catch (error) {
    console.error('Error sending test notification:', error);
    return false;
  }
};

/**
 * Send a notification via WebSocket
 * @param {Object} data - The notification data
 * @returns {boolean} True if notification was sent
 */
export const sendWebSocketNotification = (data) => {
  try {
    if (!webSocketClient || !webSocketClient.isConnected()) {
      console.warn('WebSocket client not initialized or not connected');
      return false;
    }
    
    webSocketClient.sendMessage({
      type: 'notification',
      data
    });
    
    return true;
  } catch (error) {
    console.error('Error sending WebSocket notification:', error);
    return false;
  }
};

/**
 * Send a notification when a task is assigned
 * @param {Object} task - The task object
 * @returns {Promise<boolean>} True if notification was sent through any channel
 */
export const sendTaskAssignedNotification = async (task) => {
  // Allow notification on all device types including iOS
  if (!task || !task.assignedUserId) {
    console.error('Cannot send notification: No task or assignedUserId provided');
    return false;
  }
  
  let pushSuccess = false;
  let wsSuccess = false;
  
  // Prepare notification payload
  const notificationData = {
    taskId: task.id,
    assignedUserId: task.assignedUserId,
    description: task.description,
    priority: task.priority,
    title: 'New Task Assigned',
    body: `You have been assigned a new task: ${task.description}`
  };
  
  // First try server-side push notification
  try {
    const response = await apiFetch('/api/notifications/notify-task-assigned', {
      method: 'POST',
      body: JSON.stringify(notificationData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to send task notification: ${response.status} ${response.statusText}`);
    }
    
    console.log('Task notification sent successfully via server');
    pushSuccess = true;
  } catch (error) {
    console.error('Error sending task notification via server:', error);
    
    // If server push notification failed, try WebSocket as fallback
    console.log('Attempting to send notification via WebSocket as fallback...');
    wsSuccess = sendWebSocketNotification(notificationData);
    
    if (wsSuccess) {
      console.log('Task notification sent successfully via WebSocket fallback');
    } else {
      console.warn('WebSocket fallback also failed');
    }
  }
  
  // Return true if either method succeeded
  return pushSuccess || wsSuccess;
};

/**
 * Initialize the notification system
 * Should be called when the app starts
 */
export const initializeNotifications = async () => {
  // Initialize WebSocket connection
  try {
    // Get WebSocket URL using the utility function
    const wsUrl = getWebSocketUrl('/ws');
    
    // Initialize the WebSocket connection
    webSocketClient.connect(wsUrl);
    console.log('WebSocket connection initialized for notifications');
  } catch (error) {
    console.error('Failed to initialize WebSocket for notifications:', error);
  }
  
  // Skip if notifications aren't supported
  if (!isPushSupported()) {
    console.log('Push notifications are not supported in this browser');
    return;
  }
  
  // Get the current permission status
  const permission = await getNotificationPermissionStatus();
  
  // For iOS, we take a slightly different approach but still proceed
  if (isIOS()) {
    console.log('Initializing notifications for iOS device');
    
    // If we have permission, proceed with iOS-specific initialization
    if (permission === 'granted') {
      const userId = localStorage.getItem('username');
      if (userId) {
        // Use the iOS-specific path in updateUserSubscription
        await updateUserSubscription(userId);
      }
    }
    
    window._notificationsInitialized = true;
    return;
  }
  
  // For non-iOS devices, proceed with normal initialization
  if (permission === 'granted') {
    // If we have permission, get the user ID from storage
    const userId = localStorage.getItem('username');
    if (userId) {
      // Ensure we have an up-to-date subscription for this device
      await updateUserSubscription(userId);
    }
  }
};

/**
 * Safely initialize notifications after login
 * This can be called after successful login without risking refresh loops
 */
export const initializeNotificationsAfterLogin = async () => {
  // Force a clean initialization by resetting stored subscription state
  // This ensures we always try to create a new record in the database
  localStorage.removeItem('push_subscription_active');
  localStorage.removeItem('push_subscription_user');
  localStorage.removeItem('push_subscription_device');
  console.log('🔄 Reset subscription state to force new database record creation');
  
  // Skip if already initialized in this session (but not based on localStorage)
  if (window._notificationsInitialized) {
    console.log('Notifications already initialized in this session');
    return;
  }
  
  // Set a small delay to ensure UI is settled first
  setTimeout(async () => {
    const userId = localStorage.getItem('username');
    const token = localStorage.getItem('token');
    
    // Ensure WebSocket connection with authentication
    try {
      // Get WebSocket URL with auth token using the utility function
      const wsUrl = token 
        ? `${getWebSocketUrl('/ws')}?token=${token}`
        : getWebSocketUrl('/ws');
      
      // Check if already connected, if not establish connection
      if (!webSocketClient.isConnected()) {
        webSocketClient.connect(wsUrl);
        console.log('WebSocket connection established after login');
      } else {
        // If already connected, send authentication message
        if (token && userId) {
          webSocketClient.sendMessage({
            type: 'authenticate',
            data: { 
              token,
              userId
            }
          });
          console.log('WebSocket authentication sent after login');
        }
      }
    } catch (error) {
      console.error('Failed to initialize WebSocket after login:', error);
    }
    
    if (userId) {
      // Generate a device name if not already set
      if (!localStorage.getItem('deviceName')) {
        const randomId = Math.floor(Math.random() * 10000);
        const deviceName = `${navigator.platform} Browser ${randomId}`;
        localStorage.setItem('deviceName', deviceName);
        console.log('Generated device name:', deviceName);
      }
      
      // Try to update subscription for all device types
      if (isIOS()) {
        console.log('Initializing notifications for iOS device');
      }
      await updateUserSubscription(userId);
    }
    window._notificationsInitialized = true;
  }, 2000); // 2-second delay
};

/**
 * Enhanced function to detect iOS version with better accuracy
 * @returns {Object} iOS version information
 */
export const getIOSVersion = () => {
  if (!isIOS()) {
    return { isIOS: false };
  }

  // Detailed version detection
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  const major = match ? parseInt(match[1], 10) : 0;
  const minor = match ? parseInt(match[2], 10) : 0;
  const patch = match && match[3] ? parseInt(match[3], 10) : 0;
  
  // Determine if this version supports Web Push API (16.4+)
  const supportsWebPush = major > 16 || (major === 16 && minor >= 4);
  
  return {
    isIOS: true,
    major,
    minor,
    patch,
    version: `${major}.${minor}.${patch}`,
    supportsWebPush,
    userAgent: navigator.userAgent
  };
};

/**
 * Development helper to test notification permissions and display
 * This function explicitly requests permissions and shows a test notification
 * Useful for development environments where permissions might not be set
 * @returns {Promise<object>} Result object with status and message
 */
export const testNotificationsInDevelopment = async () => {
  console.log('🧪 DEVELOPMENT: Testing notification permissions and display');
  
  // Check if we're in development environment
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                        window.location.hostname === 'localhost' || 
                        /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);
  
  const result = {
    environment: isDevelopment ? 'development' : 'production',
    permissionStatus: null,
    permissionGranted: false,
    notificationShown: false,
    message: ''
  };
  
  // Check current permission status
  result.permissionStatus = Notification.permission;
  console.log(`Current notification permission status: ${result.permissionStatus}`);
  
  // For iOS specifically
  if (isIOS()) {
    const iosVersion = getIOSVersion();
    console.log('📱 iOS device detected:', iosVersion);
    
    // Store that we've tested this
    localStorage.setItem('dev_notifications_tested', 'true');
    localStorage.setItem('dev_notifications_tested_at', new Date().toISOString());
    localStorage.setItem('ios_version_detected', JSON.stringify(iosVersion));
    
    // Show detailed iOS version info in result
    result.iosInfo = iosVersion;
    
    if (iosVersion.supportsWebPush) {
      result.message = `Your iOS version ${iosVersion.version} supports Web Push API. `;
    } else {
      result.message = `Your iOS version ${iosVersion.version} does NOT support Web Push API (requires 16.4+). `;
    }
  }
  
  // Request permission if needed
  if (result.permissionStatus !== 'granted') {
    try {
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      result.permissionStatus = permission;
      result.permissionGranted = permission === 'granted';
      
      if (permission === 'granted') {
        result.message = 'Notification permission granted! You can now receive notifications.';
        console.log('✅ Permission granted successfully');
      } else if (permission === 'denied') {
        result.message = 'Notification permission denied. Please enable notifications in your browser settings.';
        console.log('❌ Permission denied by user');
      } else {
        result.message = 'Notification permission request was ignored. Please try again.';
        console.log('⚠️ Permission request ignored');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      result.message = `Error requesting permission: ${error.message}`;
      return result;
    }
  } else {
    result.permissionGranted = true;
    console.log('Permission already granted');
  }
  
  // If permission granted, try to show a test notification
  if (result.permissionGranted) {
    try {
      // For iOS, verify settings are correct
      if (isIOS()) {
        // You can't verify iOS settings programmatically, so just provide instructions
        result.message = 'iOS detected: Permission granted! To ensure notifications work, please also check Settings > Safari > ' +
          `[${window.location.hostname}] > Notifications is turned on.`;
      }
      
      // Try to show a notification
      const notificationShown = await showLocalNotification(
        '🧪 Test Notification', 
        `This is a test notification sent at ${new Date().toLocaleTimeString()} in ${result.environment} mode.`,
        { tag: 'dev-test' }
      );
      
      result.notificationShown = notificationShown;
      
      if (notificationShown) {
        console.log('✅ Test notification displayed successfully');
        if (!result.message) {
          result.message = 'Test notification sent successfully! You should see it now.';
        }
      } else {
        console.log('❌ Failed to display test notification');
        if (!result.message) {
          result.message = 'Permission appears granted, but notification display failed.';
        }
      }
    } catch (error) {
      console.error('Error showing test notification:', error);
      result.message = `Error showing notification: ${error.message}`;
    }
  }
  
  console.log('Development notification test completed:', result);
  return result;
};

/**
 * Display a native notification (directly, not through push)
 * This is useful for testing or for local notifications
 * @param {string} title - The notification title
 * @param {string} body - The notification body text
 * @param {Object} options - Additional notification options
 * @returns {Promise<boolean>} True if notification was displayed
 */
export const showLocalNotification = async (title, body, options = {}) => {
  // Check if this is iOS 16.4+ which supports Web Push API and notifications
  const iOS164OrAbove = isIOS() && parseInt(
    (navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/) || [])[1]
  ) >= 16 && parseInt(
    (navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/) || [])[2] || 0
  ) >= 4;
  
  // Special log for iOS devices
  if (isIOS()) {
    if (iOS164OrAbove) {
      console.log('📱 iOS 16.4+ detected - can display notifications');
    } else {
      console.log('📱 Older iOS detected - notification support may be limited');
    }
  }
  
  // Get permission if we don't have it already
  const permissionGranted = await requestNotificationPermission();
  if (!permissionGranted) {
    console.log('Cannot show notification without permission');
    return false;
  }
  
  try {
    // For consistency, try to use the service worker to show the notification
    const registration = await getServiceWorkerRegistration();
    if (registration) {
      await registration.showNotification(title, {
        body,
        icon: '/zoomingroomin_logo-paw.png',
        badge: '/logo192.png',
        ...options
      });
    } else {
      // Fallback to regular Notification API if service worker isn't available
      new Notification(title, {
        body,
        icon: '/zoomingroomin_logo-paw.png',
        ...options
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error showing local notification:', error);
    
    // For iOS devices, try fallback to WebSocket notification if available
    if (isIOS() && webSocketClient && webSocketClient.isConnected()) {
      console.log('Attempting fallback to WebSocket notification for iOS');
      try {
        webSocketClient.sendMessage({
          type: 'notification',
          data: {
            title,
            body,
            ...options
          }
        });
        console.log('Sent notification via WebSocket fallback');
        return true;
      } catch (wsError) {
        console.error('WebSocket fallback also failed:', wsError);
      }
    }
    
    return false;
  }
};

/**
 * Reset and recreate subscription for the current device
 * This is particularly useful for fixing iOS notification issues
 * @param {boolean} forceIOS - Whether to force iOS detection regardless of device
 * @returns {Promise<Object>} Result object with success status and details
 */
export const resetSubscription = async (forceIOS = false) => {
  try {
    console.log('🔄 Resetting notification subscription for this device');
    if (forceIOS) {
      console.log('⚠️ FORCE iOS MODE ENABLED - Device will be registered as iPhone');
    }
    
    // 1. Get current user information
    const userId = localStorage.getItem('username');
    if (!userId) {
      console.error('Cannot reset subscription: No user ID found in local storage');
      return { success: false, error: 'No user ID found' };
    }
    
    // 2. First reset local subscription data
    // Clear relevant localStorage items
    localStorage.removeItem('push_subscription_active');
    localStorage.removeItem('push_subscription_user');
    localStorage.removeItem('push_subscription_created');
    localStorage.removeItem('websocket_registration');
    localStorage.removeItem('ios_push_registration');
    localStorage.removeItem('ios_using_web_push');
    
    // 3. Unsubscribe from browser push manager if available
    if (isPushSupported()) {
      const registration = await getServiceWorkerRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          console.log('Unsubscribed from browser push manager');
        }
      }
    }
    
    // 4. Call the server reset endpoint
    try {
      console.log('Calling server reset endpoint...');
      const response = await apiNotificationFetch('/api/notifications/reset-subscription', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Server reset response:', data);
      
      // 5. Create a new subscription
      console.log('Creating new subscription with clean state...');
      
      // Get device info, potentially with forced iOS flag
      const deviceInfo = getDeviceInfo(forceIOS);
      console.log('Device info for new subscription:', deviceInfo);
      
      // If this is iOS (either detected or forced), log version info
      if (deviceInfo.isIOS) {
        const iosVersion = getIOSVersion();
        console.log('📱 iOS device info during reset:', {
          ...iosVersion,
          forced: forceIOS && !iosVersion.isIOS
        });
        
        // Store iOS version in localStorage
        localStorage.setItem('ios_version_detected', JSON.stringify(iosVersion));
        
        // If we're forcing iOS, mark this in localStorage for debugging
        if (forceIOS) {
          localStorage.setItem('ios_detection_forced', 'true');
          localStorage.setItem('ios_forced_at', new Date().toISOString());
        }
      }
      
      // Force update the subscription
      // For manual overrides, the forceIOS flag will be passed inside
      // updateUserSubscription via the getDeviceInfo call
      const updateResult = await updateUserSubscription(userId);
      
      return {
        success: true,
        message: forceIOS 
          ? 'Subscription reset with forced iPhone registration' 
          : 'Subscription reset successfully',
        subscriptionUpdated: updateResult,
        serverResponse: data,
        deviceType: deviceInfo.isIOS ? 'iOS' : 'Other'
      };
    } catch (serverError) {
      console.error('Error resetting subscription on server:', serverError);
      throw serverError;
    }
  } catch (error) {
    console.error('Error resetting subscription:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Clear notification subscriptions for a specific user, all users, or the current user
 * This will force a new subscription process the next time notifications are initialized
 * @param {string} [specificUserId] - User ID to clear notifications for (optional, defaults to current user)
 * @param {boolean} [clearAll=false] - Set to true to clear all users' notification subscriptions
 * @returns {Promise<Object>} Object containing success status and count of cleared subscriptions
 */
export const clearNotificationSubscription = async (specificUserId, clearAll = false) => {
  try {
    // 1. Get user information
    const currentUserId = localStorage.getItem('username');
    const token = localStorage.getItem('token') || 'dummy-token';
    const deviceInfo = getDeviceInfo();
    
    // Determine which user ID to use
    const userId = specificUserId || currentUserId;
    
    // For current user only - handle local browser unsubscribe
    if (!specificUserId && !clearAll) {
      // 2. Try to unsubscribe from push manager if available
      if (isPushSupported()) {
        const registration = await getServiceWorkerRegistration();
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            // Unsubscribe from browser push manager
            await subscription.unsubscribe();
            console.log('Successfully unsubscribed from push notifications');
          }
        }
      }
      
      // 3. Disconnect WebSocket if connected (for current user only)
      if (webSocketClient && webSocketClient.isConnected()) {
        webSocketClient.disconnect();
        console.log('WebSocket connection closed');
      }
      
      // 4. Clear notification-related local storage (for current user only)
      localStorage.removeItem('deviceName');
      
      // 5. Reset notification initialization flag (for current user only)
      window._notificationsInitialized = false;
    }
    
    // 6. Notify the server to remove the appropriate subscriptions
    try {
      console.log('🗑️ Unsubscribing using direct notification API call');
      const response = await apiNotificationFetch('/api/notifications/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({
          userId: clearAll ? '' : userId,
          deviceInfo,
          deleteAll: clearAll
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      
      if (clearAll) {
        console.log(`All users' notification subscriptions cleared successfully (${responseData.removed || 0} subscriptions)`);
      } else if (specificUserId) {
        console.log(`Notification subscriptions cleared successfully for user: ${specificUserId}`);
      } else {
        console.log('Current user notification subscription cleared successfully');
      }
      
      return {
        success: true,
        count: responseData.removed || 0
      };
    } catch (serverError) {
      console.error('Error clearing server subscription:', serverError);
      throw serverError;
    }
  } catch (error) {
    console.error('Error clearing notification subscription:', error);
    return {
      success: false,
      error: error.message
    };
  }
};